import { useState } from 'react';
import { Helmet } from 'react-helmet-async';

interface Service {
  id: string;
  icon: string;
  title: string;
  description: string;
  features: string[];
  price: string;
  priceDetail: string;
  popular?: boolean;
}

const services: Service[] = [
  {
    id: 'hosting-web',
    icon: 'ri-server-line',
    title: 'Hosting Web',
    description: 'Alojamiento web profesional con cPanel, SSL gratis y soporte 24/7',
    features: [
      'Espacio SSD NVMe',
      'Certificado SSL gratis',
      'Panel cPanel incluido',
      'Respaldos diarios automáticos',
      'Soporte técnico 24/7'
    ],
    price: 'Desde $59.900',
    priceDetail: '+ IVA anual',
    popular: true
  },
  {
    id: 'email',
    icon: 'ri-mail-line',
    title: 'Email Corporativo',
    description: 'Correos profesionales con tu dominio. Cuentas desde 1 email a ilimitadas',
    features: [
      'Desde 10 GB de capacidad',
      'Cuentas desde 1 a ilimitadas',
      'Protección antispam',
      'Sincronización móvil',
      'Gmail $9.000/mes · Outlook $8.000/mes'
    ],
    price: 'Desde $2.990',
    priceDetail: '/mes'
  },
  {
    id: 'ssl',
    icon: 'ri-shield-check-line',
    title: 'Certificados SSL',
    description: 'Certificados SSL para proteger tu sitio web y mejorar SEO',
    features: [
      'SSL básico gratis',
      'SSL Wildcard disponible',
      'Instalación incluida',
      'Validación rápida',
      'Soporte técnico'
    ],
    price: 'Gratis',
    priceDetail: 'con hosting'
  },
  {
    id: 'cdn',
    icon: 'ri-global-line',
    title: 'CDN & Aceleración',
    description: 'Red de distribución de contenido para máxima velocidad global',
    features: [
      'Servidores globales',
      'Cache inteligente',
      'Protección DDoS',
      'Optimización de imágenes',
      'Reportes detallados'
    ],
    price: 'Desde $20.990',
    priceDetail: '/mes'
  },
  {
    id: 'backup',
    icon: 'ri-database-2-line',
    title: 'Backup & Restauración',
    description: 'Respaldos automáticos diarios con restauración fácil',
    features: [
      'Backup diario automático',
      'Retención 30 días',
      'Restauración 1 clic',
      'Almacenamiento seguro',
      'Notificaciones email'
    ],
    price: 'Desde $2.490',
    priceDetail: '/mes'
  },
  {
    id: 'diseno',
    icon: 'ri-palette-line',
    title: 'Diseño Web',
    description: 'Diseño profesional de sitios web responsive y modernos',
    features: [
      'Diseño personalizado',
      'Responsive design',
      'SEO optimizado',
      'Integración redes sociales',
      'Formularios de contacto'
    ],
    price: 'Desde $290.000',
    priceDetail: '+ IVA'
  },
  {
    id: 'mantencion',
    icon: 'ri-tools-line',
    title: 'Mantención Web',
    description: 'Mantención y actualización continua de tu sitio web',
    features: [
      'Actualizaciones mensuales',
      'Monitoreo 24/7',
      'Corrección de errores',
      'Optimización velocidad',
      'Reportes mensuales'
    ],
    price: 'Desde $39.000',
    priceDetail: '+ IVA /mes'
  }
];

const additionalServices = [
  {
    icon: 'ri-cloud-line',
    title: 'Migración Gratuita',
    description: 'Migramos tu sitio web sin costo desde tu proveedor actual'
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Soporte Prioritario',
    description: 'Atención preferente con tiempos de respuesta reducidos'
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Monitoreo Avanzado',
    description: 'Supervisión continua del rendimiento y disponibilidad'
  },
  {
    icon: 'ri-lock-2-line',
    title: 'Seguridad Avanzada',
    description: 'Firewall, detección de malware y protección DDoS'
  }
];

export default function ServiciosPage() {
  const [selectedService, setSelectedService] = useState<string | null>(null);

  const handleContratarClick = (serviceId: string) => {
    if (serviceId === 'diseno') {
      window.location.href = 'https://www.visualchile.cl/servicios';
    } else {
      window.REACT_APP_NAVIGATE('/contratar');
    }
  };

  return (
    <>
      <Helmet>
        <title>Servicios de Email Corporativo Gmail y Outlook | Soluciones Empresariales Chile</title>
        <meta name="description" content="Email corporativo profesional con Gmail desde $9.000/mes y Outlook desde $8.000/mes. Incluye almacenamiento amplio, protección antispam y sincronización móvil. Servicios de diseño web, mantención y CDN en Santiago." />
        <meta name="keywords" content="email corporativo chile, Gmail empresarial, Outlook profesional, correo corporativo santiago, email profesional chile, diseño web chile, mantención web, CDN chile" />
        <link rel="canonical" href="https://www.ehostingchile.com/servicios" />
        <meta property="og:title" content="Servicios de Email Corporativo Gmail y Outlook | Soluciones Empresariales Chile" />
        <meta property="og:description" content="Email corporativo profesional con Gmail desde $9.000/mes y Outlook desde $8.000/mes. Servicios de diseño web, mantención y CDN." />
        <meta property="og:url" content="https://www.ehostingchile.com/servicios" />
      </Helmet>
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-teal-50 via-white to-cyan-50 py-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto">
              <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <i className="ri-service-line"></i>
                Servicios Profesionales
              </div>
              <h1 className="text-5xl font-bold text-gray-900 mb-6">
                Soluciones Completas para tu Presencia Digital
              </h1>
              <p className="text-xl text-gray-600 leading-relaxed">
                Ofrecemos una amplia gama de servicios de hosting, desarrollo y mantención web para impulsar tu negocio en línea
              </p>
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {services.map((service) => (
                <div
                  key={service.id}
                  className={`relative bg-white rounded-2xl border-2 p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer ${
                    service.popular
                      ? 'border-teal-500 shadow-lg'
                      : 'border-gray-200 hover:border-teal-300'
                  } ${selectedService === service.id ? 'ring-4 ring-teal-200' : ''}`}
                  onClick={() => setSelectedService(service.id)}
                >
                  {service.popular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="bg-gradient-to-r from-teal-500 to-cyan-500 text-white px-4 py-1 rounded-full text-xs font-semibold whitespace-nowrap">
                        Más Popular
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl mb-4">
                    <i className={`${service.icon} text-2xl text-white`}></i>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>

                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">
                    {service.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                        <i className="ri-check-line text-teal-500 mt-0.5 flex-shrink-0"></i>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="pt-4 border-t border-gray-100">
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="text-2xl font-bold text-gray-900">
                        {service.price}
                      </span>
                      <span className="text-sm text-gray-500">
                        {service.priceDetail}
                      </span>
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleContratarClick(service.id);
                      }}
                      className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 text-white py-2.5 rounded-lg font-medium hover:shadow-lg transition-all duration-300 whitespace-nowrap"
                    >
                      Contratar Ahora
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Additional Services */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Servicios Adicionales Incluidos
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Beneficios extra que obtienes al contratar nuestros servicios
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {additionalServices.map((service, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-6 border border-gray-200 hover:border-teal-300 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-center w-12 h-12 bg-teal-100 rounded-lg mb-4">
                    <i className={`${service.icon} text-2xl text-teal-600`}></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    {service.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-600">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <h2 className="text-4xl font-bold text-white mb-6">
              ¿Necesitas Ayuda para Elegir?
            </h2>
            <p className="text-xl text-teal-50 mb-8 leading-relaxed">
              Nuestro equipo de expertos está disponible para asesorarte y encontrar la solución perfecta para tu proyecto
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/contratar')}
                className="bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold hover:shadow-xl transition-all duration-300 whitespace-nowrap"
              >
                Solicitar Cotización
              </button>
              <button
                onClick={() => window.REACT_APP_NAVIGATE('/soporte')}
                className="bg-teal-700 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-800 transition-all duration-300 whitespace-nowrap"
              >
                Contactar Soporte
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}